import { useEffect, useState, useMemo } from "react";
import { useBillingNavigation } from "../hooks/useBillingNavigation";
import { ReactComponent as XIcon } from "assets/icons/x-icon.svg";
import { RiMoneyDollarCircleFill } from "@remixicon/react";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";
import { getErrorFromFetcherData } from "utils/fetcherUtils";
import { fmtNumber } from "utils";
import { getMaxSpendingCap } from "utils/planUtils";
import Messages from "misc/Messages";
import Modal from "components/Modal";
import Button from "components/Button";
import WarningBox from "components/WarningBox";
import { trackBillingV2ModalOpened, trackBillingV2PaygChangeSubmitted, trackBillingV2PaygChangeSucceeded } from "utils/Tracking";
import { PricingOptionsDataRoutePath } from "routes/data-routes/billing-data/pricing-options/route.meta";


const { ratePerThousand } = Messages.billingV2.payg;

const MIN_SPENDING_CAP_USD = 6;

export default function PayAsYouGoModal() {
  const { goBack } = useBillingNavigation();
  const { activeSubscription } = useActiveSubscription();
  const { pricingOptionsFetcher } = usePricingOptions();

  const [spendingCap, setSpendingCap] = useState<number>(MIN_SPENDING_CAP_USD);
  const [selectedOption, setSelectedOption] = useState<'payg' | 'payg-and-sales'>('payg');

  // Show toast on success
  useToasterFromActionData(pricingOptionsFetcher);

  useCallbackAfterSubmission(() => {
    trackBillingV2PaygChangeSucceeded({ enabled: true, source: 'payg_modal' });
    goBack();
  }, pricingOptionsFetcher);

  const paygError = pricingOptionsFetcher?.state === "idle"
    ? getErrorFromFetcherData(pricingOptionsFetcher)
    : undefined;

  useEffect(() => { trackBillingV2ModalOpened('payg_enable'); }, []);

  const maxSpendingCap = useMemo(() => getMaxSpendingCap(activeSubscription), [activeSubscription]);

  // Calculate credits from spending cap
  const creditsCap = useMemo(() => {
    if (spendingCap) {
      return Math.floor((spendingCap * 1000) / ratePerThousand / 1000) * 1000;
    }
    return 0;
  }, [spendingCap]);

  // Validation — upper bound optional so enterprise / custom plans aren't blocked.
  const isValidCap = spendingCap >= MIN_SPENDING_CAP_USD &&
                     (maxSpendingCap === undefined || spendingCap <= maxSpendingCap);

  const handleEnable = () => {
    if (!pricingOptionsFetcher) return;

    trackBillingV2PaygChangeSubmitted({ enabled: true, source: 'payg_modal' });

    const formData = new FormData();
    formData.append("pricing-option-action", "pay-as-you-go");
    formData.append("pay-as-you-go", "true");
    formData.append("pay-as-you-go-cap", spendingCap.toString());
    formData.append("pay-as-you-go-credits-cap", creditsCap.toString());
    formData.append("pay-as-you-go-option",
      selectedOption === 'payg' ? "pay-as-you-go" : "pay-as-you-go-and-contact-sales"
    );

    pricingOptionsFetcher.submit(formData, {
      method: "POST",
      action: PricingOptionsDataRoutePath
    });
  };

  return (
    <Modal
      className="!max-w-[600px]"
      onClose={goBack}
    >
      <div className="flex flex-col gap-6 p-6">
        {/* Header with close button */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            Enable pay-as-you-go
          </h2>
          <button
            onClick={goBack}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <XIcon className="w-3 h-3" />
          </button>
        </div>

        {/* Subtitle */}
        <p className="text-base text-gray-600">
          Avoid service interruptions when you exceed your plan limits.
        </p>

        {/* Combined card with description and radio options */}
        <div className="bg-primary-50 border border-primary-100 rounded-lg p-5 flex flex-col gap-5">
          {/* Description */}
          <p className="text-sm text-gray-700">
            Get additional credits at <span className="font-semibold">${ratePerThousand} per {fmtNumber(1000)} credits</span> plus
            optional personalised guidance. This prevents your service from being interrupted.
          </p>

          {/* Radio options */}
          <div className="flex flex-col gap-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="payg-option"
                value="payg"
                checked={selectedOption === 'payg'}
                onChange={() => setSelectedOption('payg')}
                className="mt-1 w-5 h-5 text-brandPrimary focus:ring-brandPrimary border-gray-400"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">
                  Enable additional use
                </div>
                <div className="text-sm text-gray-600">
                  Get credits at ${ratePerThousand} per {fmtNumber(1000)} credits
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="payg-option"
                value="payg-and-sales"
                checked={selectedOption === 'payg-and-sales'}
                onChange={() => setSelectedOption('payg-and-sales')}
                className="mt-1 w-5 h-5 text-brandPrimary focus:ring-brandPrimary border-gray-400"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">
                  Enable additional use and contact sales
                </div>
                <div className="text-sm text-gray-600">
                  Same as above, plus we'll connect you with sales for tailored guidance
                </div>
              </div>
            </label>
          </div>
        {/* Spending cap input - outside the card */}
        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-900">
            Spending cap (USD)
          </label>
          <div className="relative">
            <RiMoneyDollarCircleFill className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-200" />
            <input
              type="number"
              min={MIN_SPENDING_CAP_USD}
              max={maxSpendingCap}
              value={spendingCap}
              onChange={(e) => setSpendingCap(e.target.valueAsNumber)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg
                       bg-white text-gray-900
                       focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
            />
          </div>
          <p className="text-sm text-gray-600">
            We'll stop charging once you reach this limit
          </p>
        </div>

        {paygError && (
          <WarningBox
            variant="danger"
            title="Failed to enable pay-as-you-go"
            items={[{ text: paygError.message || "Something went wrong. Please try again." }]}
          />
        )}

        <Button
          text={pricingOptionsFetcher?.state === "submitting" ? "ENABLING..." : "ENABLE PAY-AS-YOU-GO"}
          onClick={handleEnable}
          className="button button-primary w-full"
          size="MD"
          disabled={!isValidCap || pricingOptionsFetcher?.state === "submitting"}
        />
        </div>
      </div>
    </Modal>
  );
}
